import { useEffect, useEffectEvent } from "react";
import { Text } from "@simplybusiness/mobius";
import { useInterventions } from "@simplybusiness/mobius-interventions";
import classNames from "classnames/dedupe";
import type { FlattenedSectionProps } from ".";
import { useVisibilityMap } from "../../../features/questionnaire";
import { useEnvironmentValues } from "../../../hooks/environment/useEnvironment";
import { useLocale } from "../../../hooks/useLocale";
import { useShouldAnimateQuestions } from "../../../hooks/useShouldAnimateQuestions";
import { useShouldHideSectionTitle } from "../../../hooks/useShouldHideSectionTitle";
import { Preamble, Postamble } from "../Amble";
import { Question } from "./Question";
import { isQuestion } from "./Question/types";
import {
  getReactInterventionComponent,
  InterventionWrapper,
} from "@simplybusiness/mobius-interventions";
import "./Section.css";

export const Section = ({ ref, ...props }: FlattenedSectionProps) => {
  const {
    id,
    title,
    preamble,
    postamble,
    variant,
    preambleName,
    questions,
    className,
    interventions = [],
  } = props;

  const isStackedSection = variant === "stacked-sections";
  const { site } = useEnvironmentValues();
  const { locale } = useLocale();
  const shouldAnimate = useShouldAnimateQuestions();
  const shouldHideTitle = useShouldHideSectionTitle(id);
  const visibilityMap = useVisibilityMap();
  const { getInterventions } = useInterventions();

  const hasVisibleQuestions = questions.some(
    q => visibilityMap[q.name] ?? q.visible,
  );

  const getInterventionsEvent = useEffectEvent(() => {
    if (!isStackedSection && interventions.length > 0) void getInterventions();
  });

  useEffect(() => {
    void getInterventionsEvent();
  }, []);

  if (!hasVisibleQuestions) {
    return null;
  }

  const classes = classNames("mobius", "mobius-section", className);

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      {title && !shouldHideTitle && (
        <Text
          variant="title"
          elementType="h2"
          className="mobius-section__title"
        >
          {title}
        </Text>
      )}
      <Preamble
        amble={preamble}
        ambleName={preambleName}
        site={site}
        locale={locale}
      />
      <div className="mobius-section__question-container">
        {questions.filter(isQuestion).map(question => (
          <Question
            key={question.name}
            {...question}
            shouldAnimate={shouldAnimate && question.shouldAnimate !== false}
          />
        ))}
      </div>
      {interventions.map((intervention: string) => {
        const interventionElement = getReactInterventionComponent(intervention);

        if (interventionElement) {
          return (
            <InterventionWrapper key={intervention} name={intervention}>
              {interventionElement}
            </InterventionWrapper>
          );
        }
        return null;
      })}
      <Postamble amble={postamble} site={site} locale={locale} />
    </section>
  );
};

Section.displayName = "Section";
